import { HttpClient, HttpParams } from '@angular/common/http';
import { ElementRef, EventEmitter, Inject, Injectable, OnInit, ViewChild } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { SharedApiService } from '../services/api.service';
import { EmailSingleSnippetResponse, EmailSnippetResponse, GoogleEventsResponse, GoogleSingleEventResponse, UserFromBE } from '../../index';
import { environment } from './../../../../../apps/sameta-fe/src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class GoogleService {
  auth2: any;
  // public requestGoogleData = new EventEmitter<any>();
  currentGoogleUserSubject: BehaviorSubject<any | null> = new BehaviorSubject<any | null>(null);
  currentGoogleUser$ = this.currentGoogleUserSubject.asObservable();

  // Google login status observable
  private googleLoginStatusSubject = new BehaviorSubject<boolean>(
    localStorage.getItem('googleLoggedIn') !== null
  );
  googleLoginStatus$ = this.googleLoginStatusSubject.asObservable();

  loginGoogleURL = 'api/google/login';
  getLoginURL = 'api/google/get-login';
  getEmailsURL = 'api/google/get-emails';
  getEmailsOrThreadURL = 'api/google/get-emails-or-thread';
  createEventURL = 'api/google/create-event';
  getEventsURL = 'api/google/get-events';
  sendEmailURL = 'api/google/send-email';
  updateEventURL = 'api/google/update-event';
  deleteEventURL = 'api/google/delete-event';

  private oauthFocusHandler: (() => void) | null = null;

  constructor(private http: HttpClient, private sharedApiService: SharedApiService) {}

  getUrl() {
    // Remove any existing focus listener first
    if (this.oauthFocusHandler) {
      window.removeEventListener('focus', this.oauthFocusHandler);
    }

    // Mark that OAuth flow is starting
    sessionStorage.setItem('googleOAuthInProgress', 'true');

    // Create and store focus listener to detect when user returns from Google OAuth
    this.oauthFocusHandler = () => {
      // Only check if OAuth was in progress
      if (sessionStorage.getItem('googleOAuthInProgress') === 'true') {
        // Clear the flag
        sessionStorage.removeItem('googleOAuthInProgress');

        // Small delay to ensure OAuth callback has processed
        setTimeout(() => {
          // Call backend to verify actual connection status
          this.loginGoogle('null').subscribe(
            (res) => {
              // Success - user is connected
              localStorage.setItem('googleLoggedIn', 'true');
              this.googleLoginStatusSubject.next(true);

              // Remove listener after successful check
              if (this.oauthFocusHandler) {
                window.removeEventListener('focus', this.oauthFocusHandler);
                this.oauthFocusHandler = null;
              }
            },
            (err) => {
              // Connection failed or not established yet
              localStorage.removeItem('googleLoggedIn');
              this.googleLoginStatusSubject.next(false);

              // Remove listener after failed check
              if (this.oauthFocusHandler) {
                window.removeEventListener('focus', this.oauthFocusHandler);
                this.oauthFocusHandler = null;
              }
            }
          );
        }, 500);
      }
    };

    // Add focus listener
    window.addEventListener('focus', this.oauthFocusHandler);

    // Open Google OAuth consent page and get URL
    return this.http.get<any>(environment.openApiUrl + this.getLoginURL).subscribe((res) => {
      window.open(res.body, '_blank');
    });
  }

  isLoggedIn() {
    return localStorage.getItem('googleLoggedIn') !== null ? true : false;
  }

  loginGoogle(token: string): Observable<any> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('code', token);
    const headers = this.sharedApiService.getHTTPHeaders();

    return this.http.get<any>(environment.openApiUrl + this.loginGoogleURL, { headers: headers, params: queryParams });
  }

  sendEmail(data: any): Observable<EmailSingleSnippetResponse> {
    const headers = this.sharedApiService.getHTTPHeaders();

    return this.http.post<any>(environment.openApiUrl + this.sendEmailURL, JSON.stringify(data), { headers: headers });
  }

  getEmails(label: string): Observable<EmailSnippetResponse> {
    const headers = this.sharedApiService.getHTTPHeaders();
    let queryParams = new HttpParams();
    queryParams = queryParams.append('label', label);

    return this.http.get<any>(environment.openApiUrl + this.getEmailsURL, { headers: headers, params: queryParams });
  }

  getThread(threadId: string): Observable<EmailSnippetResponse> {
    const headers = this.sharedApiService.getHTTPHeaders();
    let queryParams = new HttpParams();
    queryParams = queryParams.append('threadId', threadId);

    return this.http.get<any>(environment.openApiUrl + this.getEmailsURL, { headers: headers, params: queryParams });
  }

  getEmailsOrThread(email: string): Observable<EmailSnippetResponse> {
    const headers = this.sharedApiService.getHTTPHeaders();
    let queryParams = new HttpParams();
    queryParams = queryParams.append('email', email);

    return this.http.get<any>(environment.openApiUrl + this.getEmailsOrThreadURL, { headers: headers, params: queryParams });
  }

  createEvent(data: any) {
    const headers = this.sharedApiService.getHTTPHeaders();

    return this.http.post<GoogleSingleEventResponse>(environment.openApiUrl + this.createEventURL, JSON.stringify(data), { headers: headers });
  }

  getEvents(email: string) {
    const headers = this.sharedApiService.getHTTPHeaders();
    let queryParams = new HttpParams();
    queryParams = queryParams.append('email', email);

    return this.http.get<GoogleEventsResponse>(environment.openApiUrl + this.getEventsURL, { headers: headers, params: queryParams });
  }

  updateEvent(data: any) {
    const headers = this.sharedApiService.getHTTPHeaders();

    return this.http.post<GoogleSingleEventResponse>(environment.openApiUrl + this.updateEventURL, JSON.stringify(data), { headers: headers });
  }

  deleteEvent(data: any) {
    const headers = this.sharedApiService.getHTTPHeaders();

    return this.http.post<GoogleSingleEventResponse>(environment.openApiUrl + this.deleteEventURL, JSON.stringify(data), { headers: headers });
  }

  /**
   * Disconnect Google Workspace integration
   * Opens Google Account permissions page where user can revoke access
   */
  disconnectGoogle(): void {
    // Open Google Account permissions page in new tab
    window.open('https://myaccount.google.com/permissions', '_blank');
  }

  sendGoogleUserData(user: any) {
    this.currentGoogleUserSubject.next(user);
  }
}
